import { LinkText } from '../render/link-text-node';
import { createRectNode } from '../render/rect-node';
import { createTextNode } from '../render/text-node';
import {
    isLinkText,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/utils/link';

import { Container, InteractionEvent, Cursor } from '@tencent/alloy-renderer';
import { FillTextOption, TextElement } from '@tencent/alloy-editor';
import { TextDirectionType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';

const TextHoverColor = '#00000012';
const LinkHoverId = 'link_hover_id';

interface LinkOption {
    getLinkColor: (textElement: TextElement) => string;
    onLinkMouseEnter: (e: InteractionEvent) => void;
    onLinkMouseLeave: (e: InteractionEvent) => void;
    getTextDirection: () => TextDirectionType;
    onLinkClick?: (e: InteractionEvent) => void;
    onLinkTouchEnd?: (e: InteractionEvent) => void;
    linkCursor?: Cursor;
}

export class LinkHandler {
    private waitRemoveHoverContainer: Container | null;
    private onLinkMouseEnter: LinkOption['onLinkMouseEnter'];
    private onLinkMouseLeave: LinkOption['onLinkMouseLeave'];
    private getLinkColor: LinkOption['getLinkColor'];
    private onLinkClick: LinkOption['onLinkClick'];
    private onLinkTouchEnd: LinkOption['onLinkTouchEnd'];
    private getTextDirection: LinkOption['getTextDirection'];
    private linkCursor?: Cursor;
    public constructor({
        getLinkColor,
        onLinkMouseEnter,
        onLinkMouseLeave,
        onLinkClick,
        onLinkTouchEnd,
        getTextDirection,
        linkCursor,
    }: LinkOption) {
        this.getLinkColor = getLinkColor;
        this.onLinkMouseEnter = onLinkMouseEnter;
        this.onLinkMouseLeave = onLinkMouseLeave;
        this.onLinkClick = onLinkClick;
        this.onLinkTouchEnd = onLinkTouchEnd;
        this.getTextDirection = getTextDirection;
        this.linkCursor = linkCursor;
    }

    public getLinkNode = (
        text: string,
        option: FillTextOption,
        container: Container,
    ) => this.createLinkNode(text, option, container);

    public isLinkText = (textElement: TextElement|undefined) => isLinkText(textElement);

    /**
     * 渲染链接文字节点
     * 和普通文字一样，不过增加了一些额外的事件监听
     * @param text
     * @param option
     * @returns
     */
    private createLinkNode = (text: string, option: FillTextOption, container: Container) => {
        const textElement = option.textElement as TextElement;
        const linkOption = {
            ...option,
            underline: !textElement.href?.iconMode || option.underline,
        };
        linkOption.color = this.getLinkColor(textElement);
        const textNode = createTextNode(
            text,
            linkOption,
            this.getTextDirection(),
            (text, option) => new LinkText(text, option, textElement),
            !!textElement.href?.iconMode,
        );
        if (this.linkCursor) {
            textNode.cursor = this.linkCursor;
        }
        const needHoverStyle = textElement.href?.iconMode && textElement.inlineProps;
        this.addLinkListener(textNode, container, needHoverStyle, option);
        return textNode;
    };

    private addLinkListener = (textNode, container, needHoverStyle, option) => {
        textNode.onMouseEnter((e) => {
            this.onLinkMouseEnter(e);
        });
        textNode.onMouseLeave((e) => {
            this.onLinkMouseLeave(e);
        });
        textNode.onClick((e) => {
            e.stopPropagation();
            this.onLinkClick?.(e);
        });
        textNode.onTouchEnd((e) => {
            e.stopPropagation();
            this.onLinkTouchEnd?.(e);
        });
        if (needHoverStyle) {
            container.onMouseEnter((e) => {
                e.stopPropagation();
                this.addHoverStyles(container, option);
            });
            container.onMouseLeave((e) => {
                e.stopPropagation();
                this.removeHoverStyles(container);
            });
        }
    };

    private addHoverStyles = (container, textOption) => {
        const nodes = container.children.filter(node => (node as any).id === LinkHoverId);
        if (nodes.length === 0) {
            const { boxX, boxY, height, width } = textOption;
            const rectNode = createRectNode({
                ...textOption,
                x: boxX,
                y: boxY,
                height: textOption.rotate ? textOption.height : height,
                width: textOption.rotate ? width : textOption.width,
                color: TextHoverColor,
            }, LinkHoverId);
            rectNode && container.addChildAt(rectNode, 0);
        }
        if (!this.waitRemoveHoverContainer) {
            this.waitRemoveHoverContainer = container;
            this.addDomListener();
        }
    };
    private removeHoverStyles = (container) => {
        const nodes = container.children.filter(node => (node as any).id === LinkHoverId);
        nodes.forEach((node) => {
            container.removeChild(node);
            this.waitRemoveHoverContainer = null;
            this.removeDomListener();
        });
    };

    // 由于canvas渲染引擎偶现丢失mouseleave事件，这里用document进行兜底
    private addDomListener = () => {
        document.addEventListener('mouseover', this.removeAllHoverStyles);
    };
    private removeDomListener = () => {
        document.removeEventListener('mouseover', this.removeAllHoverStyles);
    };

    private removeAllHoverStyles = () => {
        if (!this.waitRemoveHoverContainer) return;
        this.removeHoverStyles(this.waitRemoveHoverContainer);
        // 执行完就清除监听，避免内存泄漏
        this.waitRemoveHoverContainer = null;
        this.removeDomListener();
    };
}
